<template>
  <div class="space-y-3 rounded-lg border border-gray-700 bg-gray-900 p-4">
    <div class="border-b border-gray-700 pb-2 text-sm font-semibold text-gray-300">Projector Statistics</div>

    <!-- Debug info -->
    <div class="text-xs text-gray-500" v-if="!projectorStats">
      Selected ID: {{ selectionStore.selectedObjectId || 'None' }}
      <br />
      Projector Found: {{ selectedProjector ? 'Yes' : 'No' }}
      <br />
      Object Type: {{ selectedProjector?.type || 'Unknown' }}
    </div>

    <div>{{ projectorData?.brand?.name }} - {{ projectorData?.name }}</div>

    <!-- Lens Selector -->
    <div v-if="availableLenses.length > 0" class="space-y-1">
      <div class="text-xs text-gray-400">Lens</div>
      <DSelect
        v-model="selectedLensCode"
        :options="availableLenses"
        optionLabel="name"
        optionValue="code"
        placeholder="Select a lens"
        fluid
        class="text-xs" />
    </div>

    <div class="grid grid-cols-2 gap-4 text-xs" v-if="projectorStats">
      <!-- Distance -->
      <div class="space-y-1">
        <div class="text-gray-400">Distance</div>
        <div class="font-mono text-white">{{ formattedDistance }}</div>
      </div>

      <!-- Pixel Size -->
      <div class="space-y-1">
        <div class="text-gray-400">Pixel Size</div>
        <div class="font-mono text-white">{{ formattedPixelSize }}</div>
      </div>

      <!-- Throw Ratio -->
      <div class="space-y-1">
        <div class="text-gray-400">Throw Ratio</div>
        <div class="font-mono text-white">{{ formattedThrowRatio }}</div>
      </div>

      <!-- Angle -->
      <div class="space-y-1">
        <div class="text-gray-400">Angle</div>
        <div class="font-mono text-white">{{ formattedAngle }}</div>
      </div>

      <!-- Resolution -->
      <div class="space-y-1">
        <div class="text-gray-400">Resolution</div>
        <div class="font-mono text-white">{{ formattedResolution }}</div>
      </div>

      <!-- Projection Size -->
      <div class="space-y-1">
        <div class="text-gray-400">Width</div>
        <div class="font-mono text-white">{{ formattedWidth }}</div>
      </div>

      <div class="space-y-1">
        <div class="text-gray-400">Height</div>
        <div class="font-mono text-white">{{ formattedHeight }}</div>
      </div>

      <!-- Lumens -->
      <div class="space-y-1">
        <div class="text-gray-400">Lumens</div>
        <div class="font-mono text-white">{{ formattedLumens }}</div>
      </div>

      <!-- Brightness -->
      <div class="space-y-1">
        <div class="text-gray-400">Brightness</div>
        <div class="font-mono text-white">{{ formattedBrightness }}</div>
      </div>

      <!-- Lux at Center -->
      <div class="space-y-1">
        <div class="text-gray-400">Lux (Center)</div>
        <div class="font-mono text-white">{{ formattedLux }}</div>
      </div>
    </div>

    <div v-else class="py-4 text-center text-sm text-gray-500">Select a projector to view statistics</div>
  </div>
</template>

<script setup>
import { useNodeManager } from '@/composables/useNodeManager';
import { useUnitConversion } from '@/composables/useUnitConversion';
import { useCruxModuleStore } from '@/stores/api/cruxmodules';
import { useProjectorStore } from '@/stores/api/projector';
import { useProjectorLensStore } from '@/stores/api/projectorlens';
import { useSelectionStore } from '@/stores/ui/selection';
import { calculateProjectorStats } from '@/utils/projectorCalculations';
import { DSelect } from '@disguise/cloud-ui';
import { computed, ref, watch } from 'vue';

const selectionStore = useSelectionStore();
const { findNodeById, updateNodeData } = useNodeManager();
const projectorStore = useProjectorStore();
const lensStore = useProjectorLensStore();
const { formatLength } = useUnitConversion();
const cruxModuleStore = useCruxModuleStore();

const projectorData = ref(null);
const availableLenses = ref([]);
const selectedLensCode = ref(null);

// Get the selected node - this will reactively update when position changes
const selectedNode = computed(() => {
  const selectedId = selectionStore.selectedObjectId;
  if (!selectedId) return null;
  return findNodeById(selectedId);
});

const projectorStats = computed(() => {
  const node = selectedNode.value;
  if (!node) return null;

  // Use the shared calculation utility
  // Access projectorData.value to make this reactive to library data changes
  return calculateProjectorStats(node, projectorData.value);
});

const formattedDistance = computed(() => {
  if (!projectorStats.value) return '—';
  return formatLength(projectorStats.value.distance, 2);
});

const formattedPixelSize = computed(() => {
  if (!projectorStats.value) return '—';
  const pixelSizeMeters = projectorStats.value.pixelSize;

  return `${pixelSizeMeters.toFixed(2)} mm/px`;
});

const formattedThrowRatio = computed(() => {
  if (!projectorData.value && !projectorStats.value) return '—';
  const throwRatio = projectorData.value?.throw_ratio || projectorStats.value?.throwRatio || 1.4;
  return `${throwRatio}:1`;
});

const formattedAngle = computed(() => {
  if (!projectorStats.value) return '—';
  return `${projectorStats.value.spotlightAngle}°`;
});

const formattedResolution = computed(() => {
  if (!projectorData.value && !projectorStats.value) return '—';
  if (projectorData.value?.resolution_width && projectorData.value?.resolution_height) {
    return `${projectorData.value.resolution_width}×${projectorData.value.resolution_height}`;
  }
  return projectorStats.value?.resolution || 'HD';
});

const formattedWidth = computed(() => {
  if (!projectorStats.value) return '—';
  return formatLength(projectorStats.value.projectionWidth, 2);
});

const formattedHeight = computed(() => {
  if (!projectorStats.value) return '—';
  return formatLength(projectorStats.value.projectionHeight, 2);
});

const formattedLumens = computed(() => {
  if (!projectorData.value && !projectorStats.value) return '—';
  const lumens = projectorData.value?.lumens || projectorStats.value?.lumens || 5000;
  return `${lumens.toLocaleString()}`;
});

const formattedBrightness = computed(() => {
  if (!projectorStats.value) return '—';
  return `${projectorStats.value.brightnessPercent}%`;
});

const formattedLux = computed(() => {
  if (!projectorStats.value) return '—';
  const lux = projectorStats.value.luxAtCenter;
  if (lux >= 1000) {
    return `${(lux / 1000).toFixed(1)}k lux`;
  } else {
    return `${lux.toFixed(0)} lux`;
  }
});

const luxAtCenterRounded = computed(() => {
  if (!projectorStats.value) return null;
  const lux = projectorStats.value.luxAtCenter;
  if (!Number.isFinite(lux)) return null;
  return Math.max(1, Math.round(lux));
});

// Note: No longer auto-setting photometricMaxValue - user should set this manually
// as a threshold for the photometric analysis color ramp

watch(
  () => {
    const selectedId = selectionStore.selectedObjectId;
    const node = findNodeById(selectedId);
    return {
      selectedId,
      libraryCode: node?.data?.libraryCode,
      lensCode: node?.data?.lensCode,
    };
  },
  async (newValue, oldValue) => {
    // Only fetch if libraryCode actually changed
    if (newValue.libraryCode !== oldValue?.libraryCode) {
      if (newValue.libraryCode) {
        projectorStore.fetch(newValue.libraryCode).then(async (resp) => {
          projectorData.value = resp;

          const lenses = await lensStore.fetchByProjector(newValue.libraryCode);

          // Fetch available lenses if the projector has lenses
          if (resp?.lenses && resp.lenses.length > 0) {
            const lenses = await lensStore.fetchByIds(resp.lenses);
            availableLenses.value = lenses;
          } else {
            availableLenses.value = [];
          }
        });
      } else {
        projectorData.value = null;
        availableLenses.value = [];
      }
    }

    // Set the selected lens from node data (only if lensCode changed)
    if (newValue.lensCode !== oldValue?.lensCode) {
      selectedLensCode.value = newValue.lensCode || null;
    }
  },
  { immediate: true, deep: true }
);

// Watch for lens selection changes and update node data
watch(selectedLensCode, (newLensCode) => {
  const selectedId = selectionStore.selectedObjectId;
  if (selectedId && newLensCode) {
    updateNodeData(selectedId, { lensCode: newLensCode });
  }
});
</script>
